import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Field, FieldArray } from "formik";
import {
  C,
  SEMI,
  REG,
  staggerIn,
  fadeUp,
  Section,
  SegLabel,
  FieldBlock,
  FloatLabel,
  RequiredStar,
  StyledInput,
  ErrorLine,
} from "../Styles";
import moment from "moment";
import { states } from "../../../../helpers/constants";
import CustomSelect from "../../../common/CustomSelect/CustomSelect";
import { PAYMENT_MODES } from "../../data/paymentModes";

const CONTACT_FIELDS = [
  {
    name: "Name",
    label: "Full Name",
    type: "text",
    placeholder: "Your full name",
    required: true,
  },
  {
    name: "Email",
    label: "Email Address",
    type: "email",
    placeholder: "you@example.com",
    required: true,
  },
  {
    name: "Phone",
    label: "Phone Number",
    type: "tel",
    placeholder: "+91 98765 43210",
    required: true,
  },
  {
    name: "caseReference",
    label: "Case Reference",
    type: "text",
    placeholder: "NPAC-ARB-2025-XXXX",
    required: false,
  },
];

const ARBITRATOR_FIELDS = [
  {
    name: "Name",
    label: "Full Name",
    type: "text",
    placeholder: "Arbitrator full name",
    required: true,
  },
  {
    name: "Email",
    label: "Email",
    type: "email",
    placeholder: "email@example.com",
    required: false,
  },
  {
    name: "Phone",
    label: "Phone",
    type: "tel",
    placeholder: "10-digit",
    required: false,
  },
];

const ADDRESS_FIELDS = [
  {
    name: "houseNumber",
    label: "Address",
    type: "text",
    placeholder: "Your address",
    required: true,
  },
  {
    name: "locality",
    label: "Locality",
    type: "text",
    placeholder: "Your locality",
    required: true,
  },
  {
    name: "city",
    label: "City",
    type: "text",
    placeholder: "Your city",
    required: true,
  },
  {
    name: "state",
    label: "State",
    type: "select",
    required: true,
    menuItems: states,
  },
];

const getDuplicateIndices = (items = [], field) => {
  const seen = new Map();
  const dupeIndices = new Set();
  items.forEach((item, idx) => {
    const raw = (item?.[field] || "").toString().trim().toLowerCase();
    if (!raw) return;
    if (seen.has(raw)) {
      dupeIndices.add(idx);
      dupeIndices.add(seen.get(raw));
    } else {
      seen.set(raw, idx);
    }
  });
  return dupeIndices;
};

const getDuplicateFlagsMap = (items = []) => {
  const emailDupes = getDuplicateIndices(items, "Email");
  const phoneDupes = getDuplicateIndices(items, "Phone");
  return items.map((_, index) => ({
    Email: emailDupes.has(index),
    Phone: phoneDupes.has(index),
  }));
};

const DuplicateWarningBanner = ({ emailDupe, phoneDupe }) => {
  if (!emailDupe && !phoneDupe) return null;
  const parts = [];
  if (emailDupe) parts.push("email address");
  if (phoneDupe) parts.push("phone number");
  const label = parts.join(" and ");
  return (
    <DupeBanner>
      <DupeBannerIcon>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path
            d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M12 9v4M12 17h.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </DupeBannerIcon>
      <div>
        <DupeBannerTitle>Duplicate {label} detected</DupeBannerTitle>
        <DupeBannerMsg>
          This {label} is already used by another entry in this list. Please use
          a unique {label}.
        </DupeBannerMsg>
      </div>
    </DupeBanner>
  );
};

const CapacityWarningBanner = ({ capacity, total, remaining }) => {
  if (remaining > 0) return null;
  return (
    <CapacityBanner>
      <CapacityBannerIcon>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M12 8v4M12 16h.01"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </CapacityBannerIcon>
      <div>
        <CapacityBannerTitle>Room capacity reached</CapacityBannerTitle>
        <CapacityBannerMsg>
          This room holds a maximum of <strong>{capacity}</strong> participants.
          You currently have <strong>{total}</strong> claimant
          {total !== 1 ? "s" : ""} + respondent
          {total !== 1 ? "s" : ""} combined. Remove an existing entry to add
          more.
        </CapacityBannerMsg>
      </div>
    </CapacityBanner>
  );
};

const MissingAddressFields = ({ values, setFieldValue }) => {
  return (
    <AddressGroup>
      <AddressGroupTitle>
        <AddressGroupIcon>
          {/* <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M12 8v4M12 16h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg> */}
        </AddressGroupIcon>
        Address Details
      </AddressGroupTitle>

      <AddressGroupMsg>
        Please verify or update your address details before proceeding.
      </AddressGroupMsg>

      <AddressGrid $count={ADDRESS_FIELDS.length}>
        {ADDRESS_FIELDS.map(
          ({ name, label, type, placeholder, required, menuItems }) => (
            <Field key={name} name={name}>
              {({ field, meta }) => (
                <FieldBlock>
                  <FloatLabel>
                    {label}
                    {required && <RequiredStar>*</RequiredStar>}
                  </FloatLabel>

                  {type === "select" ? (
                    <CustomSelect
                      name={field.name}
                      value={field.value || ""}
                      onChange={(e) =>
                        setFieldValue(field.name, e.target.value)
                      }
                      menuItemValues={menuItems}
                    />
                  ) : (
                    <StyledInput
                      {...field}
                      type={type}
                      placeholder={placeholder}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                      $err={meta.touched && !!meta.error}
                    />
                  )}

                  {meta.touched && meta.error && (
                    <ErrorLine>{meta.error}</ErrorLine>
                  )}
                </FieldBlock>
              )}
            </Field>
          ),
        )}
      </AddressGrid>
    </AddressGroup>
  );
};

const KindSelector = ({ values, setFieldValue }) => {
  const kind = values.kind || "individual";
  const isIndividual = kind === "individual";
  const isOrganization = kind === "organization";

  const docLabel = isIndividual ? "PAN Number" : "GSTIN";
  const docPlaceholder = isIndividual ? "ABCDE1234F" : "22AAAAA0000A1Z5";
  const docPattern = isIndividual
    ? "[A-Z]{5}[0-9]{4}[A-Z]{1}"
    : "[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}";

  return (
    <KindBlock>
      <KindLabelRow>
        <KindLabelText>
          ENTITY TYPE <RequiredStar>*</RequiredStar>
        </KindLabelText>
      </KindLabelRow>

      <KindCardRow>
        {[
          {
            value: "individual",
            icon: (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            ),
            title: "Individual",
            subtitle: "PAN card optional",
          },
          {
            value: "organization",
            icon: (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                <line x1="12" y1="12" x2="12" y2="16" />
                <line x1="10" y1="14" x2="14" y2="14" />
              </svg>
            ),
            title: "Organization",
            subtitle: "GSTIN required",
          },
        ].map(({ value, icon, title, subtitle }) => {
          const active = kind === value;
          return (
            <KindCard
              key={value}
              $active={active}
              type="button"
              onClick={() => setFieldValue("kind", value)}
              role="radio"
              aria-checked={active}
            >
              <KindRadioOuter $active={active}>
                {active && <KindRadioInner />}
              </KindRadioOuter>
              <KindCardIcon $active={active}>{icon}</KindCardIcon>
              <KindCardText>
                <KindCardTitle $active={active}>{title}</KindCardTitle>
                <KindCardSub $active={active}>{subtitle}</KindCardSub>
              </KindCardText>
              {active && <KindActiveTick>✓</KindActiveTick>}
            </KindCard>
          );
        })}
      </KindCardRow>

      <DocFieldWrap $show={true} key={kind}>
        <Field name="documentNumber">
          {({ field, meta }) => (
            <FieldBlock>
              <FloatLabel>
                {docLabel} {isOrganization && <RequiredStar>*</RequiredStar>}
                <DocBadge $org={isOrganization}>
                  {isIndividual ? "Individual" : "Organization"}
                </DocBadge>
              </FloatLabel>
              <DocInputWrap>
                <DocInputIcon $active={!!field.value}>
                  {isIndividual ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  ) : (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  )}
                </DocInputIcon>
                <StyledInput
                  {...field}
                  type="text"
                  placeholder={docPlaceholder}
                  pattern={docPattern}
                  maxLength={isIndividual ? 10 : 15}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  $err={meta.touched && !!meta.error}
                  style={{
                    paddingLeft: "38px",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                />
              </DocInputWrap>
              {meta.touched && meta.error ? (
                <ErrorLine>{meta.error}</ErrorLine>
              ) : (
                <DocHint>
                  {isIndividual
                    ? "Format: ABCDE1234F — 10 characters (5 letters · 4 digits · 1 letter)"
                    : "Format: 22AAAAA0000A1Z5 — 15-character GST Identification Number"}
                </DocHint>
              )}
            </FieldBlock>
          )}
        </Field>
      </DocFieldWrap>
    </KindBlock>
  );
};

const PaymentModeSelector = ({ values, setFieldValue }) => {
  const selectedMode = values.modeOfPayment || "";

  return (
    <KindBlock>
      <KindLabelRow>
        <KindLabelText>
          PAYMENT MODE <RequiredStar>*</RequiredStar>
        </KindLabelText>
      </KindLabelRow>

      <KindCardRow>
        {PAYMENT_MODES.map(({ id, label, icon, typeOfPayment }) => {
          const active = selectedMode === id;

          return (
            <KindCard
              key={id}
              $active={active}
              type="button"
              onClick={() => setFieldValue("modeOfPayment", id)}
              role="radio"
              aria-checked={active}
            >
              <KindRadioOuter $active={active}>
                {active && <KindRadioInner />}
              </KindRadioOuter>

              <KindCardText>
                <KindCardTitle $active={active}>{label}</KindCardTitle>
                <KindCardSub $active={active}>
                  {typeOfPayment === "offline"
                    ? "Offline Payment"
                    : "Online Payment"}
                </KindCardSub>
              </KindCardText>

              {active && <KindActiveTick>✓</KindActiveTick>}
            </KindCard>
          );
        })}
      </KindCardRow>
    </KindBlock>
  );
};

// ─── PartySection ─────────────────────────────────────────────────────────────

const PartySection = ({
  arrayName,
  label,
  values,
  fields,
  cols = "1fr 1fr",
  roomCapacity,
}) => {
  const dupeFlagsList = useMemo(
    () => getDuplicateFlagsMap(values[arrayName]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values[arrayName]],
  );

  const totalParticipants =
    (values.claimants_list?.length || 0) +
    (values.respondents_list?.length || 0);
  const remaining =
    roomCapacity != null ? roomCapacity - totalParticipants : Infinity;
  const atCapacity = remaining <= 0;

  return (
    <FieldArray name={arrayName}>
      {({ push, remove }) => (
        <Section $delay="0.05s">
          <SegLabel>
            {label}
            {roomCapacity != null && (
              <CapacityPill $warn={atCapacity}>
                {values[arrayName]?.length || 0} / {roomCapacity} capacity
              </CapacityPill>
            )}
          </SegLabel>

          {atCapacity && (
            <CapacityWarningBanner
              capacity={roomCapacity}
              total={totalParticipants}
              remaining={remaining}
            />
          )}

          <CardsStack>
            {values[arrayName].map((_, index) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const dupeFlags = dupeFlagsList[index] || {
                Email: false,
                Phone: false,
              };
              return (
                <ContactShell
                  key={index}
                  $hasDupe={dupeFlags.Email || dupeFlags.Phone}
                >
                  <ContactHead>
                    <ContactHeadLeft>
                      <ContactBadge>{index + 1}</ContactBadge>
                      {label.replace(/s$/, "")} {index + 1}
                    </ContactHeadLeft>
                    {index > 0 && (
                      <RemoveBtn type="button" onClick={() => remove(index)}>
                        Remove
                      </RemoveBtn>
                    )}
                  </ContactHead>

                  <DuplicateWarningBanner
                    emailDupe={dupeFlags.Email}
                    phoneDupe={dupeFlags.Phone}
                  />

                  <ContactGrid $cols={cols}>
                    {fields.map(
                      ({
                        name,
                        label: fLabel,
                        type,
                        placeholder,
                        required,
                      }) => {
                        const fieldName = `${arrayName}[${index}].${name}`;
                        const isDupeField =
                          (name === "Email" && dupeFlags.Email) ||
                          (name === "Phone" && dupeFlags.Phone);
                        return (
                          <Field key={fieldName} name={fieldName}>
                            {({ field, meta }) => (
                              <FieldBlock>
                                <FloatLabel>
                                  {fLabel}
                                  {required && <RequiredStar>*</RequiredStar>}
                                </FloatLabel>
                                <StyledInput
                                  {...field}
                                  type={type}
                                  placeholder={placeholder}
                                  autoComplete="off"
                                  autoCorrect="off"
                                  autoCapitalize="off"
                                  spellCheck="false"
                                  data-lpignore="true"
                                  data-1p-ignore="true"
                                  data-bwignore="true"
                                  data-form-type="other"
                                  $err={
                                    (meta.touched && !!meta.error) ||
                                    isDupeField
                                  }
                                />
                                {meta.touched && meta.error && (
                                  <ErrorLine>{meta.error}</ErrorLine>
                                )}
                                {isDupeField && !meta.error && (
                                  <DupeFieldHint>
                                    Duplicate{" "}
                                    {name === "Email"
                                      ? "email address"
                                      : "phone number"}
                                  </DupeFieldHint>
                                )}
                              </FieldBlock>
                            )}
                          </Field>
                        );
                      },
                    )}
                  </ContactGrid>
                </ContactShell>
              );
            })}
          </CardsStack>

          <AddBtn
            type="button"
            disabled={atCapacity}
            $disabled={atCapacity}
            onClick={() => {
              if (!atCapacity) {
                push(fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {}));
              }
            }}
            title={
              atCapacity
                ? `Room capacity of ${roomCapacity} participants reached`
                : `Add ${label.replace(/s$/, "")}`
            }
          >
            <AddIcon>+</AddIcon>
            {atCapacity
              ? `Capacity Full (${roomCapacity} max)`
              : `Add ${label.replace(/s$/, "")}`}
          </AddBtn>
        </Section>
      )}
    </FieldArray>
  );
};

// ─── ArbitratorsSection ───────────────────────────────────────────────────────

const ArbitratorsSection = ({ values, setFieldValue }) => {
  const count = values.noOfArbitrators || 1;
  const dupeFlagsList = useMemo(
    () => getDuplicateFlagsMap(values.arbitrators_list),
    [values.arbitrators_list],
  );

  return (
    <FieldArray name="arbitrators_list">
      {({ push, remove }) => {
        const handleCountToggle = (newCount) => {
          setFieldValue("noOfArbitrators", newCount);
          const current = values.arbitrators_list || [];
          if (newCount > current.length) {
            for (let i = current.length; i < newCount; i++) {
              push({ Name: "", Email: "", Phone: "", caseReference: "" });
            }
          } else {
            for (let i = current.length - 1; i >= newCount; i--) {
              remove(i);
            }
          }
        };

        return (
          <Section $delay="0.08s">
            <SegLabel>Arbitrators</SegLabel>
            <ArbToggleWrap>
              <ArbToggleLabel>
                NUMBER OF ARBITRATORS <ArbRequired>*</ArbRequired>
              </ArbToggleLabel>
              <ArbToggleStrip>
                {[1, 3].map((n) => (
                  <ArbToggleBtn
                    key={n}
                    type="button"
                    $active={count === n}
                    onClick={() => handleCountToggle(n)}
                  >
                    {n} Arbitrator{n > 1 ? "s" : ""}
                  </ArbToggleBtn>
                ))}
              </ArbToggleStrip>
            </ArbToggleWrap>

            <CardsStack>
              {(values.arbitrators_list || []).map((_, index) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const dupeFlags = dupeFlagsList[index] || {
                  Email: false,
                  Phone: false,
                };
                return (
                  <ContactShell
                    key={index}
                    $hasDupe={dupeFlags.Email || dupeFlags.Phone}
                  >
                    <ContactHead>
                      <ContactHeadLeft>
                        <ContactBadge $teal>{index + 1}</ContactBadge>
                        Arbitrator #{index + 1}
                      </ContactHeadLeft>
                    </ContactHead>

                    <DuplicateWarningBanner
                      emailDupe={dupeFlags.Email}
                      phoneDupe={dupeFlags.Phone}
                    />

                    <ContactGrid $cols="1fr 1fr 1fr">
                      {ARBITRATOR_FIELDS.map(
                        ({
                          name,
                          label: fLabel,
                          type,
                          placeholder,
                          required,
                        }) => {
                          const fieldName = `arbitrators_list[${index}].${name}`;
                          const isDupeField =
                            (name === "Email" && dupeFlags.Email) ||
                            (name === "Phone" && dupeFlags.Phone);
                          return (
                            <Field key={fieldName} name={fieldName}>
                              {({ field, meta }) => (
                                <FieldBlock>
                                  <FloatLabel>
                                    {fLabel}
                                    {required ? (
                                      <RequiredStar>*</RequiredStar>
                                    ) : (
                                      <OptionalTag>(optional)</OptionalTag>
                                    )}
                                  </FloatLabel>
                                  <StyledInput
                                    {...field}
                                    type={type}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                    data-lpignore="true"
                                    data-1p-ignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                    placeholder={placeholder}
                                    $err={
                                      (meta.touched && !!meta.error) ||
                                      isDupeField
                                    }
                                  />
                                  {meta.touched && meta.error && (
                                    <ErrorLine>{meta.error}</ErrorLine>
                                  )}
                                  {isDupeField && !meta.error && (
                                    <DupeFieldHint>
                                      Duplicate{" "}
                                      {name === "Email"
                                        ? "email address"
                                        : "phone number"}
                                    </DupeFieldHint>
                                  )}
                                </FieldBlock>
                              )}
                            </Field>
                          );
                        },
                      )}
                    </ContactGrid>
                  </ContactShell>
                );
              })}
            </CardsStack>
          </Section>
        );
      }}
    </FieldArray>
  );
};

// ─── StepReview ───────────────────────────────────────────────────────────────

const StepReview = ({ values, setFieldValue }) => {
  const formatCurrency = (num) => {
    const value = Number(num || 0);
    return value.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const roomCapacity = values.hearing_room?.capacity ?? undefined;

  /*

  const hasMissingAddress = ADDRESS_FIELDS.some(({ name }) => {
    const val = values[name];
    return !val || val.toString().trim() === "";
  });

  */

  return (
    <>
      <Section $delay="0s">
        <SegLabel>Booking Summary</SegLabel>
        <SummaryShell>
          <Banner>
            <BannerTop>
              <div>
                <BannerLabel>Hearing Room</BannerLabel>
                <BannerRoom>
                  {values.hearing_room?.displayName || "—"}
                </BannerRoom>
              </div>
              <StatusPill>
                <StatusDot />
                Steps 1–4 Complete
              </StatusPill>
            </BannerTop>
            <QuickRow>
              <QuickStat>
                <QLabel>Date</QLabel>
                <QVal>
                  {new Date(values.hearing_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  }) || "—"}
                </QVal>
              </QuickStat>
              <QuickStat>
                <QLabel>Start</QLabel>
                <QVal>
                  {moment(values.start_time, "HH:mm:ss").format("hh:mm A") ||
                    "—"}
                </QVal>
              </QuickStat>
              {values.endTime && (
                <QuickStat>
                  <QLabel>End</QLabel>
                  <QVal>{values.endTime}</QVal>
                </QuickStat>
              )}
              <QuickStat>
                <QLabel>Duration</QLabel>
                <QVal>
                  {values.session_duration === "halfday"
                    ? "3 Hours"
                    : "6 Hours"}
                </QVal>
              </QuickStat>
              <QuickStat>
                <QLabel>Capacity</QLabel>
                <QVal>{values.hearing_room?.capacity || "—"} + 3 arb</QVal>
              </QuickStat>
              <QuickStat>
                <QLabel>Area</QLabel>
                <QVal>{values.hearing_room?.size || "—"}</QVal>
              </QuickStat>
            </QuickRow>
          </Banner>

          <SummaryBody>
            {/* ── Booked By ── */}
            <Group $d="0s">
              <GroupTitle>Booked By (verified)</GroupTitle>
              <ItemsRow $cols="repeat(3,1fr)">
                {values?.name && (
                  <Item>
                    <ItemKey>Name</ItemKey>
                    <ItemVal>{values.name}</ItemVal>
                  </Item>
                )}
                {values?.email && (
                  <Item>
                    <ItemKey>Email</ItemKey>
                    <ItemVal>{values.email}</ItemVal>
                  </Item>
                )}
                <Item>
                  <ItemKey>Mobile</ItemKey>
                  <ItemVal>{values.phone}</ItemVal>
                  <ItemVal>{values.phone}</ItemVal>
                </Item>
              </ItemsRow>
            </Group>

            {/* ── Missing Address Fields — shown ABOVE Entity Type, only if any field is empty ── */}
            <Group $d="0.04s">
              <GroupTitle>Address Information</GroupTitle>
              <MissingAddressFields
                values={values}
                setFieldValue={setFieldValue}
              />
            </Group>

            {/* ── Entity Type + Document Number ── */}
            <Group $d="0.06s">
              <GroupTitle>Entity & Tax Information</GroupTitle>
              <KindSelector values={values} setFieldValue={setFieldValue} />
            </Group>
          </SummaryBody>
        </SummaryShell>
      </Section>

      <PartySection
        arrayName="claimants_list"
        label="Claimants"
        values={values}
        fields={CONTACT_FIELDS}
        cols="1fr 1fr"
        roomCapacity={roomCapacity}
      />

      <PartySection
        arrayName="respondents_list"
        label="Respondents"
        values={values}
        fields={CONTACT_FIELDS}
        cols="1fr 1fr"
        roomCapacity={roomCapacity}
      />

      <ArbitratorsSection values={values} {...{ setFieldValue }} />

      <Group $d="0.06s">
        <PaymentModeSelector values={values} setFieldValue={setFieldValue} />
      </Group>

      <Section $delay="0.1s">
        <SegLabel>Payment</SegLabel>
        <PayCard>
          <PayTopBar>
            <PayTitle>Payment Required</PayTitle>
          </PayTopBar>
          <PayBody>
            <BreakdownList>
              <PayLineRow>
                <PayLineKey>
                  Room Booking —{" "}
                  {values.session_duration === "halfday"
                    ? "3 Hours"
                    : "6 Hours"}
                </PayLineKey>
                <PayLineVal>₹ {values?.hearing_room?.resolvedPrice}</PayLineVal>
              </PayLineRow>
              {Object.values(values.supportServices || {})
                .filter(Boolean)
                .map((service, index) => {
                  /*
                  if (service.type === "ON_REQUEST") price = "On Request";
                  else if (service.pricing?.offerPrice)
                    price = service.pricing.perHour
                      ? `₹ ${Number(service.pricing.offerPrice) * durationHours}`
                      : `₹ ${service.pricing.offerPrice}`;
                  else if (service.pricing?.flat)
                    price = `₹ ${service.pricing.flat}`;
                  else if (service.pricing?.perHour)
                    price = `₹ ${Number(service.pricing.perHour) * durationHours}`;
                  */
                  const durationHours =
                    values.session_duration === "fullday" ? 6 : 3;
                  const multiplier =
                    values.session_duration === "fullday" ? 2 : 1;

                  let price = "—";

                  if (service.type === "ON_REQUEST") {
                    price = "On Request";
                  } else if (service.pricing?.offerPrice) {
                    price = service.pricing.perHour
                      ? `₹ ${Number(service.pricing.offerPrice) * durationHours}`
                      : `₹ ${(Number(service.pricing.offerPrice) * multiplier).toLocaleString()}`;
                  } else if (service.pricing?.flat) {
                    price = `₹ ${(Number(service.pricing.flat) * multiplier).toLocaleString()}`;
                  } else if (service.pricing?.perHour) {
                    price = `₹ ${(Number(service.pricing.perHour) * durationHours).toLocaleString()}`;
                  }
                  return (
                    <PayLineRow key={index}>
                      <PayLineKey>{service.serviceName}</PayLineKey>
                      <PayLineVal>{price}</PayLineVal>
                    </PayLineRow>
                  );
                })}
              {Object.values(values.cateringServices || {})
                .filter(Boolean)
                .map((service, index) => {
                  const durationHours =
                    values.session_duration === "fullday" ? 6 : 3;
                  let price = "—";
                  if (service.type === "ON_REQUEST") price = "On Request";
                  else if (service.pricing?.offerPrice)
                    price = service.pricing.perHour
                      ? `₹ ${Number(service.pricing.offerPrice) * durationHours}`
                      : `₹ ${service.pricing.offerPrice}`;
                  else if (service.pricing?.flat)
                    price = `₹ ${service.pricing.flat}`;
                  else if (service.pricing?.perHour)
                    price = `₹ ${Number(service.pricing.perHour) * durationHours}`;
                  return (
                    <PayLineRow key={index}>
                      <PayLineKey>{service.serviceName}</PayLineKey>
                      <PayLineVal>{price}</PayLineVal>
                    </PayLineRow>
                  );
                })}
            </BreakdownList>
            <TotalBlock>
              <TotalKey>Total Due Now (GST 18% included)</TotalKey>
              <TotalVal>₹ {formatCurrency(values.total_amount)}</TotalVal>
            </TotalBlock>
            <SecureLine>
              <SecureDot />
              <SecureText>Secure payment powered by Razorpay.</SecureText>
            </SecureLine>
          </PayBody>
        </PayCard>
      </Section>
    </>
  );
};

export default StepReview;

// ─── Styled Components ────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const AddressGroup = styled.div`
  border-radius: 14px;
  padding: 16px 18px 18px;
  animation: ${slideIn} 0.25s ease both;
`;

const AddressGroupTitle = styled.div`
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #000
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
`;

const AddressGroupIcon = styled.div`
  // display: flex;
  // align-items: center;
  // color: #ea580c;
  // flex-shrink: 0;
`;

const AddressGroupMsg = styled.div`
  font-family: ${REG};
  font-size: 11.5px;
  // color: #c2410c;
  opacity: 0.85;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$count === 1 ? "1fr" : p.$count === 2 ? "1fr 1fr" : "1fr 1fr"};
  gap: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const KindBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const KindLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const KindLabelText = styled.div`
  font-family: ${SEMI};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.mutedMid};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const KindCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const KindCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.$active ? C.teal : C.line)};
  background: ${(p) => (p.$active ? "rgba(0,131,140,0.06)" : C.surface)};
  cursor: pointer;
  transition: all 0.22s ease;
  text-align: left;
  position: relative;
  outline: none;

  &:hover {
    border-color: ${(p) => (p.$active ? C.teal : C.navyLine)};
    background: ${(p) =>
      p.$active ? "rgba(0,131,140,0.09)" : "rgba(0,131,140,0.03)"};
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.2);
  }
`;

export const KindRadioOuter = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$active ? C.teal : C.muted)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.2s;
  background: ${C.white};
`;

export const KindRadioInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${C.teal};
  animation: ${slideIn} 0.18s ease;
`;

export const KindCardIcon = styled.div`
  color: ${(p) => (p.$active ? C.teal : C.muted)};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: color 0.2s;
`;

export const KindCardText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const KindCardTitle = styled.div`
  font-family: ${SEMI};
  font-size: 13.5px;
  font-weight: 700;
  color: ${(p) => (p.$active ? C.teal : C.navy)};
  line-height: 1.2;
  transition: color 0.2s;
  letter-spacing: -0.01em;
`;

export const KindCardSub = styled.div`
  font-family: ${REG};
  font-size: 11px;
  color: ${(p) => (p.$active ? "rgba(0,131,140,0.7)" : C.muted)};
  margin-top: 2px;
  transition: color 0.2s;
`;

export const KindActiveTick = styled.div`
  position: absolute;
  top: 10px;
  right: 12px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${C.teal};
  color: white;
  font-size: 10px;
  font-family: ${SEMI};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${slideIn} 0.2s ease;
`;

const DocFieldWrap = styled.div`
  animation: ${slideIn} 0.25s ease;
`;

const DocInputWrap = styled.div`
  position: relative;
`;

const DocInputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(p) => (p.$active ? C.teal : C.muted)};
  display: flex;
  align-items: center;
  pointer-events: none;
  transition: color 0.2s;
  z-index: 1;
`;

const DocBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$org ? "rgba(234,88,12,0.10)" : "rgba(0,131,140,0.10)"};
  color: ${(p) => (p.$org ? "#c2410c" : C.teal)};
  border: 1px solid
    ${(p) => (p.$org ? "rgba(234,88,12,0.25)" : "rgba(0,131,140,0.25)")};
  vertical-align: middle;
`;

const DocHint = styled.div`
  margin-top: 5px;
  font-family: ${REG};
  font-size: 11px;
  color: ${C.mutedLight};
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

export const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 12px;
`;

export const ContactShell = styled.div`
  background: ${C.white};
  border: 1.5px solid ${(p) => (p.$hasDupe ? "rgba(220,53,69,0.40)" : C.line)};
  border-radius: 16px;
  overflow: hidden;
  transition: border-color 0.2s;
  &:hover {
    border-color: ${(p) => (p.$hasDupe ? "rgba(220,53,69,0.60)" : C.navyLine)};
  }
`;

export const ContactHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  background: ${C.surface};
  border-bottom: 1px solid ${C.line};
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  color: ${C.navy};
`;

export const ContactHeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ContactBadge = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$teal ? C.teal : C.navy)};
  color: ${C.white};
  flex-shrink: 0;
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "1fr 1fr"};
  gap: 16px;
  padding: 22px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const RemoveBtn = styled.button`
  padding: 5px 14px;
  border-radius: 8px;
  border: 1.5px solid ${C.line};
  background: ${C.white};
  font-family: ${SEMI};
  font-size: 11.5px;
  font-weight: 600;
  color: ${C.error};
  cursor: pointer;
  transition: all 0.18s;
  &:hover {
    background: ${C.errorPale};
    border-color: ${C.error};
  }
`;

export const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 20px;
  border-radius: 10px;
  border: 1.5px dashed ${(p) => (p.$disabled ? C.line : C.tealLine)};
  background: ${(p) => (p.$disabled ? C.surface : C.tealAlpha)};
  font-family: ${SEMI};
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.$disabled ? C.muted : C.teal)};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
  margin-top: 4px;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  &:hover:not(:disabled) {
    background: rgba(0, 131, 140, 0.16);
    border-color: ${C.teal};
  }
`;

export const AddIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  font-family: ${SEMI};
`;

export const OptionalTag = styled.span`
  font-family: ${REG};
  font-size: 10px;
  color: ${C.mutedLight};
  margin-left: 2px;
  text-transform: none;
  letter-spacing: 0;
`;

const CapacityPill = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 99px;
  font-family: ${SEMI};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${(p) =>
    p.$warn ? "rgba(220,53,69,0.10)" : "rgba(0,131,140,0.10)"};
  color: ${(p) => (p.$warn ? "#dc3545" : C.teal)};
  border: 1px solid
    ${(p) => (p.$warn ? "rgba(220,53,69,0.25)" : "rgba(0,131,140,0.25)")};
  vertical-align: middle;
  transition: all 0.2s;
`;

const CapacityBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 14px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(234, 88, 12, 0.06);
  border: 1px solid rgba(234, 88, 12, 0.28);
`;

const CapacityBannerIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  color: #ea580c;
  display: flex;
  align-items: center;
`;

const CapacityBannerTitle = styled.div`
  font-family: ${SEMI};
  font-size: 12px;
  font-weight: 700;
  color: #c2410c;
  margin-bottom: 2px;
`;

const CapacityBannerMsg = styled.div`
  font-family: ${REG};
  font-size: 11.5px;
  line-height: 1.5;
  color: #c2410c;
  opacity: 0.9;
  strong {
    font-family: ${SEMI};
    font-weight: 700;
  }
`;

const DupeBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 14px 16px 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(220, 53, 69, 0.06);
  border: 1px solid rgba(220, 53, 69, 0.25);
`;

const DupeBannerIcon = styled.div`
  flex-shrink: 0;
  margin-top: 1px;
  color: #dc3545;
  display: flex;
  align-items: center;
`;

const DupeBannerTitle = styled.div`
  font-family: ${SEMI};
  font-size: 12px;
  font-weight: 700;
  color: #b91c2c;
  margin-bottom: 2px;
  text-transform: capitalize;
`;

const DupeBannerMsg = styled.div`
  font-family: ${REG};
  font-size: 11.5px;
  line-height: 1.5;
  color: #b91c2c;
  opacity: 0.88;
`;

const DupeFieldHint = styled.div`
  margin-top: 4px;
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 600;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 4px;
  &::before {
    content: "⚠";
    font-size: 10px;
  }
`;

export const ArbToggleWrap = styled.div`
  margin-bottom: 20px;
`;

export const ArbToggleLabel = styled.div`
  font-family: ${SEMI};
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.mutedMid};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ArbRequired = styled.span`
  color: #ff3c3c;
  font-size: 12px;
`;

export const ArbToggleStrip = styled.div`
  display: flex;
  border: 1.5px solid ${C.line};
  border-radius: 12px;
  overflow: hidden;
  background: ${C.surface};
`;

export const ArbToggleBtn = styled.button`
  flex: 1;
  padding: 14px 20px;
  border: none;
  background: ${(p) => (p.$active ? C.teal : "transparent")};
  font-family: ${SEMI};
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.$active ? C.white : C.navy)};
  cursor: pointer;
  transition: all 0.22s ease;
  border-right: 1.5px solid ${C.line};
  letter-spacing: -0.01em;
  &:last-child {
    border-right: none;
  }
  &:hover:not(:disabled) {
    background: ${(p) => (p.$active ? C.tealDark : C.tealAlpha)};
    color: ${(p) => (p.$active ? C.white : C.teal)};
  }
`;

const SummaryShell = styled.div`
  border-radius: 18px;
  overflow: hidden;
  border: 1.5px solid ${C.line};
  background: ${C.white};
`;

const Banner = styled.div`
  background: linear-gradient(
    135deg,
    ${C.navy} 0%,
    ${C.navyDark} 60%,
    ${C.navyDeep} 100%
  );
  padding: 24px 28px 20px;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(0, 131, 140, 0.12);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -60px;
    left: -30px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.03);
    pointer-events: none;
  }
`;

const BannerTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
`;

const BannerLabel = styled.div`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.38);
  margin-bottom: 5px;
`;

const BannerRoom = styled.div`
  font-family: ${SEMI};
  font-size: clamp(20px, 3vw, 24px);
  font-weight: 800;
  color: ${C.white};
  letter-spacing: -0.025em;
  line-height: 1.1;
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 13px;
  border-radius: 99px;
  background: rgba(0, 131, 140, 0.22);
  border: 1px solid rgba(0, 131, 140, 0.38);
  font-family: ${SEMI};
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.04em;
  flex-shrink: 0;
`;

const StatusDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${C.teal};
  box-shadow: 0 0 0 2.5px rgba(0, 131, 140, 0.35);
`;

const QuickRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const QuickStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const QLabel = styled.div`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.38);
`;

const QVal = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13.5px;
  color: ${C.white};
  letter-spacing: -0.01em;
`;

const SummaryBody = styled.div`
  padding: 20px 24px 8px;
  background: ${C.white};
`;

const Group = styled.div`
  margin-bottom: 20px;
  animation: ${staggerIn} 0.35s ease both;
  animation-delay: ${(p) => p.$d || "0s"};
`;

const GroupTitle = styled.div`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${C.line};
  }
`;

const ItemsRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "repeat(3,1fr)"};
  gap: 4px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const Item = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: ${C.surface};
  border: 1px solid ${C.line};
  transition: all 0.18s;
  &:hover {
    border-color: ${C.navyLine};
  }
`;

const ItemKey = styled.div`
  font-family: ${SEMI};
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-bottom: 4px;
`;

const ItemVal = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  line-height: 1.3;
  color: ${C.text};
`;

const PayCard = styled.div`
  background: linear-gradient(
    145deg,
    ${C.navy} 0%,
    ${C.navyDark} 55%,
    ${C.navyDeep} 100%
  );
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  animation: ${fadeUp} 0.38s ease 0.08s both;
  &::before {
    content: "";
    position: absolute;
    top: -80px;
    right: -80px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: rgba(0, 131, 140, 0.1);
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -100px;
    left: -50px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.025);
    pointer-events: none;
  }
`;

const PayTopBar = styled.div`
  padding: 20px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 1;
`;

const PayTitle = styled.div`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 15.5px;
  color: ${C.white};
  letter-spacing: -0.01em;
  margin-bottom: 3px;
`;

const PayBody = styled.div`
  padding: 0 28px 22px;
  position: relative;
  z-index: 1;
`;

const BreakdownList = styled.div`
  padding: 18px 0 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
`;

const PayLineRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  &:last-child {
    border-bottom: none;
  }
`;

const PayLineKey = styled.span`
  font-family: ${REG};
  font-size: 13px;
  color: rgba(255, 255, 255, 0.58);
`;

const PayLineVal = styled.span`
  font-family: ${SEMI};
  font-weight: 700;
  font-size: 13px;
  color: ${C.white};
`;

const TotalBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 20px;
`;

const TotalKey = styled.span`
  font-family: ${SEMI};
  font-size: 14px;
  font-weight: 700;
  color: ${C.tealLight};
  letter-spacing: 0.01em;
`;

const TotalVal = styled.span`
  font-family: ${SEMI};
  font-size: 22px;
  font-weight: 800;
  color: ${C.tealLight};
  letter-spacing: -0.02em;
`;

const SecureLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(0, 131, 140, 0.14);
  border-radius: 10px;
  border: 1px solid rgba(0, 131, 140, 0.24);
`;

const SecureDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${C.teal};
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.25);
`;

const SecureText = styled.span`
  font-family: ${REG};
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.55;
`;
